// Settings (docs/06 §2) — the aggregator. A list of tappable group rows that
// each navigate to their own sub-screen (Account, Notifications, Sessions,
// Language, Ringtone [native], Android [native], Developer [dev-unlocked]),
// plus the endpoint-list version. The recovery phrase lives in Account (with a
// pre-registration fallback here) and the echo test in Calls. The version
// footer unlocks Developer mode.

import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAccount } from '../../shell/accountContext.js';
import {
  appUpdateStore,
  checkForAppUpdate,
  installAppUpdate,
  updateIsWaiting,
  SETTINGS_CHECK_INTERVAL_MS,
} from '../../shell/appUpdate.js';
import { DEV_BUILD } from '../../shell/config.js';
import { isCapacitorNative } from '../../shell/capacitor/platform.js';
import { useScreens } from '../ScreensContext.js';
import { useStore } from '../ui/useStore.js';
import { DEV_UNLOCK_TAPS, isDevUnlocked, setDevUnlocked } from '../../shell/devServer.js';
import { useT, type MessageKey } from '../../i18n/index.js';
import { useIsDesktop } from '../ui/AppShell.js';
import { Section, buttonClass, quietButtonClass } from './chrome.js';
import { SettingsDialog } from './SettingsDialog.js';
import { PhraseSection } from './AccountSettingsScreen.js';

// --- Group navigation rows -------------------------------------------------------------

function NavRow({ to, label, icon }: { to: string; label: string; icon: ReactNode }): ReactNode {
  return (
    <Link
      to={to}
      className="flex min-h-touch items-center gap-3 rounded-xl bg-surface p-4 text-sm font-semibold text-ink"
    >
      <span
        aria-hidden="true"
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-raised text-accent"
      >
        {icon}
      </span>
      <span className="flex-1">{label}</span>
      <span aria-hidden="true" className="text-muted">
        ›
      </span>
    </Link>
  );
}

// Inline stroke icons (match the in-call control style: 2px stroke, round
// caps; emoji render inconsistently across OEM fonts).
const iconProps = {
  viewBox: '0 0 24 24',
  className: 'h-5 w-5',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

const AccountIcon = (
  <svg {...iconProps}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c1.8-3.2 4.6-5 8-5s6.2 1.8 8 5" />
  </svg>
);
const SecurityIcon = (
  <svg {...iconProps}>
    <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);
const NotificationsIcon = (
  <svg {...iconProps}>
    <path d="M18 10a6 6 0 0 0-12 0c0 5-2 6.5-2 6.5h16S18 15 18 10" />
    <path d="M10.3 20a2 2 0 0 0 3.4 0" />
  </svg>
);
const SessionsIcon = (
  <svg {...iconProps}>
    <rect x="3" y="4" width="13" height="10" rx="2" />
    <path d="M7 18h5" />
    <rect x="16" y="10" width="5" height="9" rx="1.5" />
  </svg>
);
const CallsIcon = (
  <svg {...iconProps}>
    <path d="M6.6 10.8c1.4 2.8 3.8 5.2 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.6.1.4 0 .8-.3 1z" />
  </svg>
);
const LanguageIcon = (
  <svg {...iconProps}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3c2.5 2.4 3.8 5.5 3.8 9S14.5 18.6 12 21c-2.5-2.4-3.8-5.5-3.8-9S9.5 5.4 12 3" />
  </svg>
);
const AndroidIcon = (
  <svg {...iconProps}>
    <path d="M4 15v-3a8 8 0 0 1 16 0v3" />
    <path d="M7 5.5 5.5 3.5M17 5.5l1.5-2" />
    <rect x="3" y="15" width="18" height="4" rx="2" />
  </svg>
);
const DeveloperIcon = (
  <svg {...iconProps}>
    <path d="m8 8-4 4 4 4" />
    <path d="m16 8 4 4-4 4" />
    <path d="m13 5-2 14" />
  </svg>
);

// --- App updates (Android self-update, docs/06 §4) ----------------------------

const UPDATE_STATUS_KEY: Record<string, MessageKey> = {
  checking: 'updates.checking',
  'up-to-date': 'updates.upToDate',
  available: 'updates.available',
  downloading: 'updates.downloading',
  'install-prompted': 'updates.installPrompted',
  'install-permission': 'updates.installPermission',
};

// The offer popup, once dismissed, stays dismissed for that version until the
// app restarts (module-lived, so tab round-trips don't nag again).
let popupDismissedVersion = 0;

/** Modal offer shown when opening Settings finds a newer version waiting. */
function UpdatePopup({
  onInstall,
  onLater,
}: {
  onInstall: () => void;
  onLater: () => void;
}): ReactNode {
  const t = useT();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="update-popup-title"
        className="flex w-full max-w-sm flex-col gap-3 rounded-2xl bg-surface p-5"
      >
        <h2 id="update-popup-title" className="text-base font-semibold text-ink">
          {t('updates.popupTitle')}
        </h2>
        <p className="text-sm text-muted">{t('updates.available')}</p>
        <div className="flex flex-col gap-2">
          <button type="button" onClick={onInstall} className={buttonClass}>
            {t('updates.install')}
          </button>
          <button type="button" onClick={onLater} className={quietButtonClass}>
            {t('updates.later')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Native-only: check against the signed endpoint list / run the installer. */
function UpdatesSection(): ReactNode {
  const t = useT();
  const { getAccessToken } = useAccount();
  const { engine } = useScreens();
  const state = useStore(appUpdateStore);
  const [dismissedBelow, setDismissedBelow] = useState(() => popupDismissedVersion);

  // Opening the Settings tab re-checks automatically (short throttle so it
  // doesn't hammer the server on every visit; a pending offer is never
  // clobbered — runUpdateCheck guards that).
  useEffect(() => {
    void checkForAppUpdate({
      getToken: getAccessToken,
      minIntervalMs: SETTINGS_CHECK_INTERVAL_MS,
      onDocument: (doc) => void engine.updateEndpointList?.(doc),
    });
  }, [getAccessToken, engine]);

  const busy = state.phase === 'checking' || state.phase === 'downloading';
  const waiting = updateIsWaiting(state);
  const statusKey: MessageKey =
    state.phase === 'error'
      ? state.reason === 'offline'
        ? 'updates.errorOffline'
        : state.reason === 'rejected'
          ? 'updates.errorRejected'
          : 'updates.errorDownload'
      : (UPDATE_STATUS_KEY[state.phase] ?? 'updates.notChecked');
  // A re-check that lands on the same answer must still visibly DO something:
  // "up to date" carries the check time, so every tap updates the line.
  const status =
    state.phase === 'up-to-date'
      ? t('updates.upToDate', {
          time: new Date(state.checkedAt).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }),
        })
      : t(statusKey);

  const offeredVersion = state.phase === 'available' ? state.info.apkVersion : 0;
  const dismissOffer = (): void => {
    popupDismissedVersion = Math.max(popupDismissedVersion, offeredVersion);
    setDismissedBelow(popupDismissedVersion);
  };

  return (
    <Section title={t('updates.title')}>
      {offeredVersion > dismissedBelow && (
        <UpdatePopup
          onInstall={() => {
            dismissOffer();
            void installAppUpdate();
          }}
          onLater={dismissOffer}
        />
      )}
      <p className={`text-xs ${waiting ? 'text-ink' : 'text-muted'}`} role="status">
        {status}
      </p>
      {state.phase === 'error' && state.detail !== undefined && (
        <p className="break-all font-mono text-[10px] text-muted">{state.detail}</p>
      )}
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          if (waiting) void installAppUpdate();
          else
            // Same document sink as the auto/launch checks: a manual check
            // must also adopt the endpoint rotation, not just the APK offer
            // (field report 2026-07-17: phone stuck on an old list version).
            void checkForAppUpdate({
              getToken: getAccessToken,
              force: true,
              onDocument: (doc) => void engine.updateEndpointList?.(doc),
            });
        }}
        className="min-h-touch rounded-xl bg-raised px-4 py-2 text-sm font-semibold text-accent disabled:opacity-50"
      >
        {waiting ? t('updates.install') : t('updates.check')}
      </button>
    </Section>
  );
}

/** Tap the version label {@link DEV_UNLOCK_TAPS} times to open Developer mode. */
function VersionFooter({ onUnlock, className }: { onUnlock: () => void; className?: string }): ReactNode {
  const t = useT();
  const [taps, setTaps] = useState(0);
  // APK builds bake the Android versionName in as VITE_APP_VERSION (Makefile),
  // so the footer always matches the manifest. `vite build` reports MODE
  // "production" even for the debug APK; DEV_BUILD marks debug builds.
  const version =
    (import.meta.env.VITE_APP_VERSION as string | undefined) ??
    (import.meta.env.MODE as string | undefined) ??
    'dev';
  const commit = import.meta.env.VITE_APP_COMMIT as string | undefined;
  // Web and native ship as separate clients from separate release channels, so
  // the footer names which one is running: "Merkury Web" identifies the PWA
  // build (whose version + commit is baked by `make frontend-build`), so a
  // deploy is verifiable at a glance. Native keeps the plain wordmark.
  const label = isCapacitorNative() ? 'Merkury' : t('settings.webClient');
  const build = DEV_BUILD ? `${version} · debug` : version;

  function tap(): void {
    const next = taps + 1;
    setTaps(next);
    if (next >= DEV_UNLOCK_TAPS) {
      setDevUnlocked(true);
      onUnlock();
    }
  }

  return (
    <button
      type="button"
      onClick={tap}
      className={`mx-auto py-2 text-center text-xs text-muted ${className ?? ''}`}
      aria-label={t('settings.appVersion')}
    >
      {label} · {t('settings.version', { version: build })}
      {commit ? ` · ${commit}` : ''}
    </button>
  );
}

// --- Screen ------------------------------------------------------------------------------------

export function SettingsScreen(): ReactNode {
  const t = useT();
  const { state } = useAccount();
  const navigate = useNavigate();
  const { engine } = useScreens();
  const engineState = useStore(engine.state);
  const [devUnlocked, setDevUnlockedState] = useState(() => isDevUnlocked());
  const native = isCapacitorNative();
  const isDesktop = useIsDesktop();

  // Only wait for the LOCAL account to load — never for server registration.
  // A device that hasn't reached a backend yet (no userId) must still get into
  // Settings, notably the Developer section, to point itself at one; gating the
  // whole screen on userId used to deadlock that (prime directive: never block
  // on the server when we can proceed locally).
  if (state.status !== 'ready') {
    return (
      <main className="mx-auto flex min-h-full w-full max-w-app items-center justify-center p-6">
        <p className="text-sm text-muted" role="status">
          {t('settings.loading')}
        </p>
      </main>
    );
  }
  const { identity } = state;
  const userId = state.account.userId;

  const onUnlock = (): void => {
    setDevUnlockedState(true);
    void navigate('/settings/developer');
  };

  // The device-only "landing" content that has no sub-screen of its own: the
  // not-connected banner, the pre-registration phrase fallback, the native
  // self-updater, the endpoint-list version, and the version footer (whose taps
  // unlock Developer mode). Shared between the compact page and the desktop
  // dialog so the dev-unlock ritual and update check work in both.
  const landing = (
    <>
      {!userId && (
        <Section title={t('settings.notConnectedTitle')}>
          <p className="text-xs text-muted">{t('settings.notConnectedBody')}</p>
        </Section>
      )}
      {!userId && <PhraseSection identity={identity} />}
      {native && <UpdatesSection />}
      <Section title={t('settings.aboutConnection')}>
        <p className="text-xs text-muted">
          {t('settings.endpointVersion')}{' '}
          <span data-testid="endpoint-list-version" className="font-mono text-ink">
            {engineState.endpointListVersion}
          </span>
        </p>
      </Section>
      <VersionFooter onUnlock={onUnlock} />
    </>
  );

  // Desktop: the whole settings surface is the overlay dialog. Its left nav
  // carries the section links (built inside SettingsDialog from the same
  // userId/native/dev-unlock gating as the rows below), so the aggregator body
  // is just the landing content — no nav rows here.
  if (isDesktop) {
    return (
      <SettingsDialog title={t('settings.title')}>{landing}</SettingsDialog>
    );
  }

  return (
    <main className="mx-auto flex min-h-full w-full max-w-app flex-col gap-4 p-6">
      {/* No back button: Settings is a navbar tab, not a pushed screen. */}
      <header className="flex items-center gap-3">
        <h1 className="text-xl font-semibold">{t('settings.title')}</h1>
      </header>

      {!userId && (
        <Section title={t('settings.notConnectedTitle')}>
          <p className="text-xs text-muted">{t('settings.notConnectedBody')}</p>
        </Section>
      )}

      <nav className="flex flex-col gap-2" aria-label={t('settings.groups')}>
        {/* Account & Sessions are server-backed — only once registered. */}
        {userId && <NavRow to="/settings/account" icon={AccountIcon} label={t('settings.account')} />}
        {userId && <NavRow to="/settings/security" icon={SecurityIcon} label={t('settings.security')} />}
        <NavRow to="/settings/notifications" icon={NotificationsIcon} label={t('settings.notifications')} />
        <NavRow to="/settings/calls" icon={CallsIcon} label={t('settings.calls')} />
        {userId && <NavRow to="/settings/sessions" icon={SessionsIcon} label={t('settings.sessions')} />}
        <NavRow to="/settings/language" icon={LanguageIcon} label={t('settings.language')} />
        {native && <NavRow to="/settings/android" icon={AndroidIcon} label={t('settings.android')} />}
        {devUnlocked && <NavRow to="/settings/developer" icon={DeveloperIcon} label={t('settings.developer')} />}
      </nav>

      {/* Local, device-only sections — always available, even pre-registration
          and offline (prime directive: never block on the server). */}
      {/* Pre-registration fallback: no userId means no Account row, but the
          recovery phrase must stay reachable offline (prime directive). Once
          registered it lives in Settings → Account. */}
      {!userId && <PhraseSection identity={identity} />}

      {/* APK self-update — only meaningful inside the Android shell (the web
          PWA updates itself via the service worker). */}
      {native && <UpdatesSection />}

      <Section title={t('settings.aboutConnection')}>
        <p className="text-xs text-muted">
          {t('settings.endpointVersion')}{' '}
          <span data-testid="endpoint-list-version" className="font-mono text-ink">
            {engineState.endpointListVersion}
          </span>
        </p>
      </Section>

      {/* Pinned to the bottom (mt-auto): on a tall phone with a short list the
          slack must sit ABOVE the footer, not leave it floating mid-screen with
          a gap below it down to the tab bar (iOS PWA field report). */}
      <VersionFooter
        className="mt-auto"
        onUnlock={() => {
          setDevUnlockedState(true);
          void navigate('/settings/developer');
        }}
      />
    </main>
  );
}
